import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity, ArrowDown, ArrowUp, AtSign, CalendarClock, CheckSquare, Euro, FileText,
  Globe, KeyRound, MessageSquare, Pencil, Plus, Send, Trash2, User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  ACTION_LABELS, api, CATEGORIES, eur, errMsg, fmtDate, fmtDateShort, fmtDateTime,
  fmtMinutes, PAY_METHODS, PRICING, PRIORITY, STATUS, TASK_STATUS,
} from "../lib/api";
import { EmptyState, PageHeader, PayBadge, PriorityBadge, StatusBadge, TaskStatusBadge, UserAvatar } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProjectForm } from "../components/ProjectForm";
import { TaskForm } from "../components/TaskForm";
import { PaymentForm, CredentialForm } from "../components/SimpleForms";
import { FileList } from "../components/FileList";
import { CredentialList } from "../components/CredentialList";
import { VideoCallButton } from "../components/VideoCallButton";

function InfoRow({ label, children }) {
  return (
    <div className="rounded-xl bg-[#F7F6F2] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#999]">{label}</p>
      <div className="mt-0.5 text-sm font-medium text-[#1D225C]">{children || "—"}</div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "uebersicht";

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [activity, setActivity] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [editingPay, setEditingPay] = useState(null);
  const [credOpen, setCredOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const commentBoxRef = useRef(null);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error(errMsg(e));
      navigate("/projekte");
    }
  }, [id, navigate]);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/tasks`);
      setTasks(data);
    } catch {}
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/comments`);
      setComments(data);
      await api.post(`/projects/${id}/comments/read`);
    } catch {}
  }, [id]);

  const loadPayments = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        api.get("/payments", { params: { project_id: id } }),
        api.get("/time-entries", { params: { project_id: id } }),
      ]);
      setPayments(p.data);
      setTimeEntries(t.data);
    } catch {}
  }, [id]);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get("/activity", { params: { project_id: id, limit: 100 } });
      setActivity(data);
    } catch {}
  }, [id]);

  const reloadAll = useCallback(() => {
    loadProject();
    loadTasks();
    loadComments();
    loadPayments();
    loadActivity();
  }, [loadProject, loadTasks, loadComments, loadPayments, loadActivity]);

  useEffect(() => {
    reloadAll();
    (async () => {
      try {
        const [c, u, p] = await Promise.all([api.get("/clients"), api.get("/users"), api.get("/projects")]);
        setClients(c.data);
        setUsers(u.data);
        setAllProjects(p.data);
      } catch {}
    })();
  }, [reloadAll]);

  useEffect(() => {
    if (tab === "kommentare") loadComments();
  }, [tab, loadComments]);

  if (!project) {
    return <div className="h-64 animate-pulse rounded-2xl bg-white" data-testid="project-detail-loading" />;
  }

  const setTab = (t) => setParams({ tab: t });

  const changeStatus = async (status) => {
    try {
      await api.patch(`/projects/${id}/status`, { status });
      toast.success(`Status: ${STATUS[status].label}`);
      loadProject();
      loadActivity();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const taskStatus = async (t, status) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { status });
      loadTasks();
      loadProject();
      loadActivity();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Aufgabe „${t.title}" löschen?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Aufgabe gelöscht");
      loadTasks();
      loadProject();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const moveTask = async (index, dir) => {
    const arr = [...tasks];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setTasks(arr);
    try {
      await api.post(`/projects/${id}/tasks/reorder`, { task_ids: arr.map((t) => t.id) });
    } catch {
      loadTasks();
    }
  };

  const sendComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/projects/${id}/comments`, { message: commentText });
      setCommentText("");
      loadComments();
      loadActivity();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveCommentEdit = async () => {
    try {
      await api.put(`/comments/${editingComment.id}`, { message: editCommentText });
      setEditingComment(null);
      loadComments();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteComment = async (c) => {
    if (!window.confirm("Kommentar löschen?")) return;
    try {
      await api.delete(`/comments/${c.id}`);
      loadComments();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Zahlung ${eur(p.amount)} löschen?`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Zahlung gelöscht");
      loadPayments();
      loadProject();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const renderMessage = (msg) =>
    msg.split(/(@\w+)/g).map((part, i) =>
      part.startsWith("@") ? (
        <span key={i} className="rounded bg-[#B88A4A]/15 px-1 font-semibold text-[#B88A4A]">{part}</span>
      ) : (
        part
      )
    );

  const totalMinutes = timeEntries.reduce((s, t) => s + (t.duration_minutes || 0), 0);

  return (
    <div className="animate-fade-up" data-testid="project-detail-page">
      {/* Header */}
      <div className="mb-6 rounded-3xl border border-[#1D225C]/10 bg-white p-6 shadow-sm" data-testid="project-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <button onClick={() => navigate("/projekte")} className="mb-1 text-xs font-medium text-[#777] hover:text-[#1D225C]" data-testid="back-to-projects">
              ← Alle Projekte
            </button>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B88A4A]" data-testid="project-client-name">
              {project.client_name}
            </p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#1D225C] sm:text-3xl" data-testid="project-name">
              {project.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Select value={project.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="project-status-select" className="h-8 w-52 rounded-xl border-[#1D225C]/15 bg-white text-xs font-semibold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PriorityBadge priority={project.priority} testid="project-priority-badge" />
              <PayBadge status={project.payment_status} testid="project-payment-badge" />
              {project.deadline && (
                <span className="flex items-center gap-1.5 rounded-full bg-[#1D225C]/5 px-2.5 py-1 text-xs font-medium text-[#1D225C]" data-testid="project-deadline">
                  <CalendarClock className="h-3.5 w-3.5 text-[#B88A4A]" /> {fmtDate(project.deadline)}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="project-edit-button" onClick={() => setEditOpen(true)} className="rounded-xl">
              <Pencil className="mr-1.5 h-4 w-4" /> Bearbeiten
            </Button>
            <Button variant="outline" data-testid="project-add-task-button" onClick={() => { setEditingTask(null); setTaskOpen(true); }} className="rounded-xl">
              <CheckSquare className="mr-1.5 h-4 w-4" /> Aufgabe
            </Button>
            <Button variant="outline" data-testid="project-goto-files-button" onClick={() => setTab("dateien")} className="rounded-xl">
              <FileText className="mr-1.5 h-4 w-4" /> Datei
            </Button>
            <Button variant="outline" data-testid="project-goto-comments-button" onClick={() => { setTab("kommentare"); setTimeout(() => commentBoxRef.current?.focus(), 100); }} className="rounded-xl">
              <MessageSquare className="mr-1.5 h-4 w-4" /> Kommentar
            </Button>
            <Button variant="outline" data-testid="project-add-payment-button" onClick={() => { setEditingPay(null); setPayOpen(true); }} className="rounded-xl">
              <Euro className="mr-1.5 h-4 w-4" /> Zahlung
            </Button>
            <Button data-testid="project-add-credential-button" onClick={() => setCredOpen(true)} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
              <KeyRound className="mr-1.5 h-4 w-4" /> Zugang
            </Button>
            <VideoCallButton projectId={id} testid="project-call-button" label="Anruf" />
          </div>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[#777]">
            <span>Fortschritt ({project.task_done}/{project.task_total} Aufgaben)</span>
            <span className="font-bold text-[#1D225C]" data-testid="project-progress-label">{project.progress}%</span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#1D225C]/10">
            <div className="h-full rounded-full bg-[#B88A4A] transition-all duration-500" style={{ width: `${project.progress}%` }} data-testid="project-progress-bar" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 flex h-auto flex-wrap justify-start gap-1 rounded-2xl border border-[#1D225C]/10 bg-white p-1.5" data-testid="project-tabs">
          {[
            ["uebersicht", "Übersicht"], ["aufgaben", `Aufgaben (${tasks.length})`],
            ["kommentare", `Kommentare (${comments.length})`], ["dateien", "Dateien"],
            ["zugaenge", "Zugänge"], ["zahlungen", "Zahlungen"], ["aktivitaet", "Aktivität"],
          ].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="rounded-xl px-3.5 py-2 text-xs font-semibold data-[state=active]:bg-[#1D225C] data-[state=active]:text-white"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ÜBERSICHT */}
        <TabsContent value="uebersicht" data-testid="tab-content-uebersicht">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <InfoRow label="Kunde">{project.client_name}</InfoRow>
                <InfoRow label="Website">
                  {project.website_url ? (
                    <a href={project.website_url.startsWith("http") ? project.website_url : `https://${project.website_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#B88A4A] hover:underline">
                      <Globe className="h-3.5 w-3.5" /> {project.website_url}
                    </a>
                  ) : "—"}
                </InfoRow>
                <InfoRow label="Projekttyp">{CATEGORIES[project.type] || project.type}</InfoRow>
                <InfoRow label="Start">{fmtDate(project.start_date)}</InfoRow>
                <InfoRow label="Deadline">{fmtDate(project.deadline)}</InfoRow>
                <InfoRow label="Zugewiesen an">
                  {project.assignee_name ? (
                    <span className="flex items-center gap-1.5">
                      <UserAvatar name={project.assignee_name} color={project.assignee_color} avatar={project.assignee_avatar} size={5} />
                      {project.assignee_name}
                    </span>
                  ) : "—"}
                </InfoRow>
                <InfoRow label="Kundenpreis">{project.price ? eur(project.price) : "—"}</InfoRow>
                <InfoRow label="Preismodell">{PRICING[project.pricing_model]}</InfoRow>
                <InfoRow label="Mondher-Honorar">
                  {project.pricing_model === "hourly"
                    ? `${eur(project.hourly_rate)}/h · aktuell ${eur(project.agreed_payment)}`
                    : project.mondher_payment ? eur(project.mondher_payment) : "—"}
                </InfoRow>
              </div>
              {project.description && (
                <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#B88A4A]">Beschreibung</p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#333]" data-testid="project-description">{project.description}</p>
                </div>
              )}
              {project.notes && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">Wichtige Notizen</p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-amber-900" data-testid="project-notes">{project.notes}</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#B88A4A]">Finanzen (Mondher)</p>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between"><span className="text-[#777]">Vereinbart</span><span className="font-semibold text-[#1D225C]">{eur(project.agreed_payment)}</span></div>
                  <div className="flex justify-between"><span className="text-[#777]">Bereits bezahlt</span><span className="font-semibold text-[#22C55E]">{eur(project.paid_to_mondher)}</span></div>
                  <div className="flex justify-between border-t border-[#1D225C]/10 pt-2.5"><span className="text-[#777]">Offen</span><span className="font-display text-base font-bold text-[#B88A4A]" data-testid="project-open-payment">{eur(project.open_payment)}</span></div>
                </div>
              </div>
              <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#B88A4A]">Nächster Schritt</p>
                <p className="text-sm text-[#1D225C]" data-testid="project-next-action">{project.next_action || "Nicht definiert"}</p>
              </div>
              <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#B88A4A]">Zeiterfassung</p>
                <p className="font-display text-xl font-bold text-[#1D225C]">{fmtMinutes(totalMinutes)}</p>
                <p className="text-xs text-[#777]">{timeEntries.length} Einträge</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* AUFGABEN */}
        <TabsContent value="aufgaben" data-testid="tab-content-aufgaben">
          <div className="mb-3 flex justify-end">
            <Button data-testid="tasks-add-button" onClick={() => { setEditingTask(null); setTaskOpen(true); }} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
              <Plus className="mr-1.5 h-4 w-4" /> Neue Aufgabe
            </Button>
          </div>
          {tasks.length === 0 ? (
            <EmptyState icon={CheckSquare} title="Noch keine Aufgaben" hint="Erstelle die erste Aufgabe für dieses Projekt." testid="tasks-empty" />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
              {tasks.map((t, i) => (
                <div key={t.id} data-testid={`task-row-${t.id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3.5 ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                  <button
                    data-testid={`task-toggle-${t.id}`}
                    onClick={() => taskStatus(t, t.status === "fertig" ? "todo" : "fertig")}
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors ${
                      t.status === "fertig" ? "border-[#22C55E] bg-[#22C55E] text-white" : "border-[#1D225C]/20 hover:border-[#22C55E]"
                    }`}
                  >
                    {t.status === "fertig" && <CheckSquare className="h-3.5 w-3.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${t.status === "fertig" ? "text-[#999] line-through" : "text-[#1D225C]"}`}>{t.title}</p>
                    <p className="text-xs text-[#777]">
                      {t.assignee_name || "Niemand"}{t.due_date ? ` · fällig ${fmtDateShort(t.due_date)}` : ""}
                      {t.estimated_hours ? ` · ~${t.estimated_hours}h` : ""}
                    </p>
                  </div>
                  <PriorityBadge priority={t.priority} />
                  <Select value={t.status} onValueChange={(v) => taskStatus(t, v)}>
                    <SelectTrigger data-testid={`task-status-${t.id}`} className="h-8 w-36 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TASK_STATUS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center">
                    <button data-testid={`task-up-${t.id}`} onClick={() => moveTask(i, -1)} className="rounded p-1 text-[#999] hover:text-[#1D225C]"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button data-testid={`task-down-${t.id}`} onClick={() => moveTask(i, 1)} className="rounded p-1 text-[#999] hover:text-[#1D225C]"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button data-testid={`task-edit-${t.id}`} onClick={() => { setEditingTask(t); setTaskOpen(true); }} className="rounded p-1 text-[#999] hover:text-[#1D225C]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`task-delete-${t.id}`} onClick={() => deleteTask(t)} className="rounded p-1 text-[#999] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* KOMMENTARE */}
        <TabsContent value="kommentare" data-testid="tab-content-kommentare">
          <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 shadow-sm">
            {comments.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#777]" data-testid="comments-empty">
                Noch keine Kommentare. Starte die Projekt-Kommunikation hier – statt WhatsApp.
              </p>
            ) : (
              <div className="space-y-4" data-testid="comments-list">
                {comments.map((c) => {
                  const own = c.user_id === user.id;
                  return (
                    <div key={c.id} data-testid={`comment-${c.id}`} className={`flex gap-3 ${own ? "flex-row-reverse" : ""}`}>
                      <UserAvatar name={c.author_name} color={c.author_color} avatar={c.author_avatar} size={9} />
                      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${own ? "bg-[#1D225C] text-white" : "bg-[#F7F6F2] text-[#1D225C]"}`}>
                        <div className={`mb-1 flex items-center gap-2 text-[11px] ${own ? "text-white/60" : "text-[#999]"}`}>
                          <span className="font-semibold">{c.author_name}</span>
                          <span>{fmtDateTime(c.created_at)}</span>
                          {c.edited_at && <span>(bearbeitet)</span>}
                        </div>
                        {editingComment?.id === c.id ? (
                          <div>
                            <Textarea data-testid="comment-edit-input" value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} rows={2} className="bg-white text-[#1D225C]" />
                            <div className="mt-2 flex gap-2">
                              <Button size="sm" data-testid="comment-edit-save" onClick={saveCommentEdit} className="rounded-lg bg-[#B88A4A] text-white hover:bg-[#a67a3f]">Speichern</Button>
                              <Button size="sm" variant="outline" data-testid="comment-edit-cancel" onClick={() => setEditingComment(null)} className="rounded-lg">Abbrechen</Button>
                            </div>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap text-sm leading-relaxed">{renderMessage(c.message)}</p>
                        )}
                        {own && !editingComment && (
                          <div className={`mt-1.5 flex gap-2 text-[11px] ${own ? "justify-end text-white/50" : ""}`}>
                            <button data-testid={`comment-edit-${c.id}`} onClick={() => { setEditingComment(c); setEditCommentText(c.message); }} className="hover:underline">Bearbeiten</button>
                            <button data-testid={`comment-delete-${c.id}`} onClick={() => deleteComment(c)} className="hover:underline">Löschen</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-5 border-t border-[#1D225C]/10 pt-4">
              <div className="mb-2 flex gap-1.5">
                {users.map((u) => (
                  <button
                    key={u.id}
                    data-testid={`mention-${u.name.split(" ")[0].toLowerCase()}`}
                    onClick={() => setCommentText((t) => `${t}@${u.name.split(" ")[0]} `)}
                    className="flex items-center gap-1 rounded-lg bg-[#B88A4A]/10 px-2 py-1 text-xs font-medium text-[#B88A4A] hover:bg-[#B88A4A]/20"
                  >
                    <AtSign className="h-3 w-3" /> {u.name.split(" ")[0]}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Textarea
                  ref={commentBoxRef}
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                  placeholder="Nachricht schreiben… (@Manuela / @Mondher für Erwähnungen)"
                  rows={2}
                  className="rounded-xl"
                />
                <Button data-testid="comment-send-button" onClick={sendComment} className="self-end rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* DATEIEN */}
        <TabsContent value="dateien" data-testid="tab-content-dateien">
          <FileList projectId={id} />
        </TabsContent>

        {/* ZUGÄNGE */}
        <TabsContent value="zugaenge" data-testid="tab-content-zugaenge">
          <CredentialList projectId={id} projects={allProjects} clients={clients} defaultProjectId={id} />
        </TabsContent>

        {/* ZAHLUNGEN */}
        <TabsContent value="zahlungen" data-testid="tab-content-zahlungen">
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { l: "Kundenpreis", v: eur(project.price), testid: "pay-card-price" },
              { l: "Mondher-Honorar", v: eur(project.agreed_payment), testid: "pay-card-agreed" },
              { l: "Bereits bezahlt", v: eur(project.paid_to_mondher), testid: "pay-card-paid" },
              { l: "Offen", v: eur(project.open_payment), testid: "pay-card-open", highlight: true },
            ].map((c) => (
              <div key={c.l} data-testid={c.testid} className={`rounded-2xl border p-4 shadow-sm ${c.highlight ? "border-[#B88A4A]/40 bg-[#B88A4A]/5" : "border-[#1D225C]/10 bg-white"}`}>
                <p className="text-xs font-medium text-[#777]">{c.l}</p>
                <p className={`mt-1 font-display text-xl font-bold ${c.highlight ? "text-[#B88A4A]" : "text-[#1D225C]"}`}>{c.v}</p>
              </div>
            ))}
          </div>
          <div className="mb-3 flex justify-end">
            <Button data-testid="payments-add-button" onClick={() => { setEditingPay(null); setPayOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
              <Plus className="mr-1.5 h-4 w-4" /> Zahlung eintragen
            </Button>
          </div>
          {payments.length === 0 ? (
            <EmptyState icon={Euro} title="Noch keine Zahlungen" hint="Trage die erste Zahlung an Mondher für dieses Projekt ein." testid="payments-empty" />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
              {payments.map((p, i) => (
                <div key={p.id} data-testid={`payment-row-${p.id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                  <span className="font-display text-base font-bold text-[#1D225C]">{eur(p.amount)}</span>
                  <span className="text-xs text-[#777]">{fmtDate(p.date)}</span>
                  <span className="rounded-lg bg-[#1D225C]/5 px-2 py-0.5 text-[11px] font-medium text-[#1D225C]">{PAY_METHODS[p.method] || p.method}</span>
                  {p.notes && <span className="min-w-0 flex-1 truncate text-xs text-[#777]">{p.notes}</span>}
                  <span className="text-xs text-[#999]">{p.created_by_name}</span>
                  <div className="ml-auto flex gap-1">
                    <button data-testid={`payment-edit-${p.id}`} onClick={() => { setEditingPay(p); setPayOpen(true); }} className="rounded p-1 text-[#999] hover:text-[#1D225C]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`payment-delete-${p.id}`} onClick={() => deletePayment(p)} className="rounded p-1 text-[#999] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {timeEntries.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#B88A4A]">Erfasste Arbeitszeit · {fmtMinutes(totalMinutes)}</p>
              <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                {timeEntries.map((t, i) => (
                  <div key={t.id} className={`flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                    <span className="text-xs text-[#777]">{fmtDate(t.date)}</span>
                    <span className="font-semibold text-[#1D225C]">{fmtMinutes(t.duration_minutes)}</span>
                    {t.start && t.end && <span className="text-xs text-[#999]">{t.start}–{t.end}</span>}
                    <span className="min-w-0 flex-1 truncate text-xs text-[#777]">{t.description}</span>
                    <span className="text-xs text-[#999]">{t.user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* AKTIVITÄT */}
        <TabsContent value="aktivitaet" data-testid="tab-content-aktivitaet">
          {activity.length === 0 ? (
            <EmptyState icon={Activity} title="Noch keine Aktivität" testid="activity-empty" />
          ) : (
            <div className="rounded-2xl border border-[#1D225C]/10 bg-white p-5 shadow-sm">
              <div className="space-y-4">
                {activity.map((a) => (
                  <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#B88A4A]" />
                    <div>
                      <p className="text-sm text-[#1D225C]">
                        <span className="font-semibold">{a.user_name}</span>
                        <span className="text-[#777]"> · {ACTION_LABELS[a.action] || a.action}</span>
                      </p>
                      {a.details && <p className="text-sm text-[#555]">{a.details}</p>}
                      <p className="text-xs text-[#999]">{fmtDateTime(a.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ProjectForm open={editOpen} onOpenChange={setEditOpen} project={project} clients={clients} users={users} onSaved={reloadAll} />
      <TaskForm open={taskOpen} onOpenChange={setTaskOpen} task={editingTask} projectId={id} users={users} onSaved={() => { loadTasks(); loadProject(); loadActivity(); }} />
      <PaymentForm open={payOpen} onOpenChange={setPayOpen} projects={allProjects} defaultProjectId={id} payment={editingPay} onSaved={() => { loadPayments(); loadProject(); loadActivity(); }} />
      <CredentialForm open={credOpen} onOpenChange={setCredOpen} projects={allProjects} clients={clients} defaultProjectId={id} onSaved={() => { if (tab === "zugaenge") setParams({ tab: "zugaenge", r: Date.now() }); }} />
    </div>
  );
}
